import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import axios from 'axios';

const brand = {
  primary: '#047857',
  accent: '#0e918c',
  text: '#0b1f1a',
};

const ComingSoonModal = ({ open, onClose }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.civixthread.com';
      
      // Use contact endpoint for waitlist signup
      await axios.post(
        `${API_BASE_URL}/api/contact/`,
        {
          name: name || email.split('@')[0] || 'App Waitlist User',
          email: email,
          subject: 'App Launch Notification Request',
          message: `User wants to be notified when the CivixThread mobile app launches. Name: ${name || 'Not provided'}`,
          request: 'app_waitlist',
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setEmail('');
        setName('');
        setSuccess(false);
      }, 2500);
    } catch (err) {
      console.error('Error submitting email:', err);
      setError('Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmail('');
      setName('');
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${brand.primary} 0%, ${brand.accent} 100%)`,
          color: '#fff',
          py: 3.5,
          px: 4,
          position: 'relative',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: 'rgba(255, 255, 255, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(255, 255, 255, 0.25)',
            }}
          >
            <PhoneIphoneRoundedIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                display: 'inline-block',
                fontSize: '0.7rem',
                fontWeight: 800,
                letterSpacing: 2.5,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                mb: 1,
              }}
            >
              COMING SOON
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2, letterSpacing: -0.5 }}>
              Get Notified When We Launch
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: '#fff',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ px: 4, py: 4 }}>
        {success ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: `${brand.primary}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <CheckCircleIcon
                sx={{
                  fontSize: 48,
                  color: brand.primary,
                }}
              />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: brand.text }}>
              You're on the list!
            </Typography>
            <Typography sx={{ color: brand.text, fontSize: '1rem', lineHeight: 1.6 }}>
              We'll notify you as soon as the CivixThread app is available for download.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography sx={{ color: brand.text, lineHeight: 1.7, fontSize: '0.95rem', mb: 3, mt: 2 }}>
              Be the first to know when the CivixThread mobile app launches! Enter your email below
              and we'll send you a notification as soon as it's available on the App Store and Google Play.
            </Typography>

            {error && (
              <Alert 
                severity="error" 
                sx={{ mb: 3, borderRadius: 2 }} 
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Name"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
                InputLabelProps={{
                  sx: { color: brand.text, fontWeight: 500 },
                }}
                sx={{
                  mb: 2.5,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    '& fieldset': {
                      borderColor: '#e5e7eb',
                      borderWidth: 1.5,
                    },
                    '&:hover fieldset': {
                      borderColor: brand.primary,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: brand.primary,
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: brand.text,
                    py: 1.75,
                  },
                }}
              />
              <TextField
                fullWidth
                type="email"
                label="Email Address *"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
                InputLabelProps={{
                  sx: { color: brand.text, fontWeight: 500 },
                }}
                sx={{
                  mb: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    '& fieldset': {
                      borderColor: '#e5e7eb',
                      borderWidth: 1.5,
                    },
                    '&:hover fieldset': {
                      borderColor: brand.primary,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: brand.primary,
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: brand.text,
                    py: 1.75,
                  },
                }}
              />
            </Box>
          </>
        )}
      </DialogContent>

      {/* Actions */}
      {!success && (
        <DialogActions 
          sx={{ 
            px: 4, 
            pb: 4, 
            pt: 0,
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(135deg, ${brand.primary} 0%, ${brand.accent} 100%)`,
              borderRadius: 2.5,
              p: '1.5px',
              '&:hover': {
                opacity: 0.9,
              },
            }}
          >
            <Button
              onClick={handleClose}
              disabled={loading}
              sx={{
                color: brand.text,
                fontWeight: 600,
                px: 3,
                py: 1.25,
                borderRadius: 2.5,
                textTransform: 'none',
                fontSize: '0.95rem',
                bgcolor: '#fff',
                width: '100%',
                '&:hover': {
                  bgcolor: '#f9fafb',
                },
                '&:disabled': {
                  bgcolor: '#f3f4f6',
                  color: '#9ca3af',
                },
              }}
            >
              Cancel
            </Button>
          </Box>
          <Button
            onClick={handleSubmit}
            disabled={loading || !email}
            variant="contained"
            sx={{
              background: `linear-gradient(135deg, ${brand.primary} 0%, ${brand.accent} 100%)`,
              color: '#fff',
              px: 4,
              py: 1.25,
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              fontSize: '0.95rem',
              boxShadow: '0 4px 14px rgba(4, 120, 87, 0.35)',
              '&:hover': {
                background: `linear-gradient(135deg, #065f46 0%, #0c7a74 100%)`,
                boxShadow: '0 6px 20px rgba(4, 120, 87, 0.45)',
              },
              '&:disabled': {
                background: '#d1d5db',
                color: '#9ca3af',
                boxShadow: 'none',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: '#fff' }} />
            ) : (
              'Notify Me'
            )}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ComingSoonModal;
